import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { itemsManagementService } from '../../../services/itemsManagementService';

const ItemFormModal = ({ isOpen, onClose, item, onSave, categories, organizationId }) => {
  const [formData, setFormData] = useState({
    itemName: '',
    description: '',
    sku: '',
    category: '',
    unitOfMeasure: 'each',
    purchasePrice: '',
    sellingPrice: '',
    taxRate: '0',
    accountId: '', // New field for account selection
    weightPerUnit: '1',
    isActive: true,
    trackInventory: true,
    supplier: '',
    barcode: '',
    weight: '',
    dimensions: {
      length: '',
      width: '',
      height: ''
    },
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [chartOfAccounts, setChartOfAccounts] = useState([]); // New state for accounts
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  // Load chart of accounts when modal opens
  useEffect(() => {
    if (isOpen && organizationId) {
      loadChartOfAccounts();
    }
  }, [isOpen, organizationId]);

  const loadChartOfAccounts = async () => {
    if (!organizationId) return;
    
    setLoadingAccounts(true);
    try {
      const accounts = await itemsManagementService?.getChartOfAccounts(organizationId);
      setChartOfAccounts(accounts || []);
    } catch (err) {
      console.error('Error loading chart of accounts:', err);
      setChartOfAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  };

  useEffect(() => {
    if (item) {
      setFormData({
        itemName: item?.name || '',
        description: item?.description || '',
        sku: item?.sku || '',
        category: item?.category || '',
        unitOfMeasure: item?.unit || 'each',
        purchasePrice: item?.price?.toString() || '',
        sellingPrice: item?.cost?.toString() || '',
        taxRate: item?.taxRate?.toString() || '0',
        accountId: item?.accountId || '', // Include account ID from item data
        weightPerUnit: item?.weight?.toString() || '1',
        isActive: item?.status === 'active',
        trackInventory: item?.trackInventory || true,
        supplier: item?.supplier || '',
        barcode: item?.barcode || '',
        weight: item?.weight?.toString() || '',
        dimensions: {
          length: item?.dimensions?.length?.toString() || '',
          width: item?.dimensions?.width?.toString() || '',
          height: item?.dimensions?.height?.toString() || ''
        },
        notes: item?.notes || ''
      });
    } else {
      // Reset form for new item - don't pre-populate SKU
      const defaultCategory = categories && categories?.length > 0 ? categories?.[0] : '';
      
      setFormData({
        itemName: '',
        description: '',
        sku: '', // Let database generate - don't pre-populate
        category: defaultCategory,
        unitOfMeasure: 'each',
        purchasePrice: '',
        sellingPrice: '',
        taxRate: '0',
        accountId: '', // Reset account selection
        weightPerUnit: '1',
        isActive: true,
        trackInventory: true,
        supplier: '',
        barcode: '',
        weight: '',
        dimensions: {
          length: '',
          width: '',
          height: ''
        },
        notes: ''
      });
    }
    setErrors({});
    setError('');
  }, [item, isOpen, categories]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.itemName?.trim()) newErrors.itemName = 'Item name is required';
    if (!formData?.category) newErrors.category = 'Category is required';
    if (!formData?.purchasePrice || isNaN(parseFloat(formData?.purchasePrice))) newErrors.purchasePrice = 'Valid purchase price is required';
    if (!formData?.sellingPrice || isNaN(parseFloat(formData?.sellingPrice))) newErrors.sellingPrice = 'Valid selling price is required';
    
    if (formData?.trackInventory) {
      if (!formData?.weightPerUnit || isNaN(parseFloat(formData?.weightPerUnit))) newErrors.weightPerUnit = 'Valid weight per unit is required';
    }

    if (formData?.taxRate && isNaN(parseFloat(formData?.taxRate))) {
      newErrors.taxRate = 'Tax rate must be a valid number';
    }

    return newErrors;
  };

  const handleChange = (field, value) => {
    if (field?.includes('dimensions')) {
      const dimensionField = field?.split('.')?.[1];
      setFormData(prev => ({
        ...prev,
        dimensions: {
          ...prev?.dimensions,
          [dimensionField]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }

    // Clear error when field is updated
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // CRITICAL FIX: Also clear the main error when user types
    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors)?.length > 0) {
      setErrors(validationErrors);
      return;
    }

    // CRITICAL FIX: Check for required fields before submission
    if (!formData?.itemName?.trim()) {
      setError('Item name is required');
      return;
    }

    if (!organizationId && !item) {
      setError('Organization ID is required to create items');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setErrors({}); // CRITICAL FIX: Clear all errors at start of submission

    try {
      // CRITICAL FIX: Ensure all required fields are properly mapped
      const itemData = {
        name: formData?.itemName?.trim(), // Ensure name is trimmed and not empty
        description: formData?.description || '',
        sku: formData?.sku?.trim() || '', // Allow empty SKU for auto-generation
        category: formData?.category,
        unit: formData?.unitOfMeasure || 'each',
        price: parseFloat(formData?.sellingPrice) || 0,
        cost: parseFloat(formData?.purchasePrice) || 0,
        taxRate: parseFloat(formData?.taxRate) || 0,
        weight: parseFloat(formData?.weightPerUnit) || 1,
        accountId: formData?.accountId || null, // Add account ID to item data
        isActive: formData?.isActive,
      };

      // CRITICAL FIX: Validate that required data exists before API call
      if (!itemData?.name) {
        setError('Item name cannot be empty');
        return;
      }

      console.log('Submitting item data:', itemData); // Debug logging

      // CRITICAL FIX: Use the onSave callback instead of direct service call
      // This ensures proper error handling flow through the parent component
      if (onSave) {
        await onSave(itemData);
        // CRITICAL FIX: If we reach here, save was successful - clear all errors and close modal
        setError('');
        setErrors({});
        // Don't close manually - let parent component handle it
      } else {
        // Fallback to direct service call if onSave not provided
        let result;
        if (item) {
          result = await itemsManagementService?.updateItem(item?.id, itemData);
        } else {
          if (!organizationId) {
            throw new Error('Organization ID is required to create items');
          }
          result = await itemsManagementService?.createItem(organizationId, itemData);
        }

        if (result?.error) {
          throw result?.error;
        }

        // CRITICAL FIX: Clear all errors on successful save
        setError('');
        setErrors({});
        onClose();
      }
      
    } catch (error) {
      console.error('Error saving item:', error);
      
      // Enhanced error handling with more specific messages
      if (error?.code === '23505' || error?.message?.includes('duplicate key')) {
        setError('Item number conflict detected. The system will auto-generate a unique number. Please try again.');
      } else if (error?.code === '23502') {
        const match = error?.message?.match(/null value in column "([^"]+)"/);
        const columnName = match ? match?.[1] : 'unknown field';
        setError(`Required field missing: ${columnName}. Please fill in all required fields and try again.`);
      } else if (error?.message?.includes('item_name')) {
        setError('Item name is required and cannot be empty. Please enter a valid item name.');
      } else if (error?.code === 'VALIDATION_ERROR') {
        // CRITICAL FIX: Don't show validation errors if item was actually saved
        // This happens when the parent component throws validation error but save succeeds
        console.warn('Validation error after save attempt:', error);
        setError('Item may have been saved. Please refresh the page to verify.');
      } else {
        setError(error?.message || 'Failed to save item. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const generateSKU = () => {
    const name = formData?.itemName?.toUpperCase()?.replace(/\s+/g, '-')?.slice(0, 6);
    const category = formData?.category?.toUpperCase()?.slice(0, 3);
    const timestamp = Date.now()?.toString()?.slice(-4);
    const sku = `${category || 'ITM'}-${name || 'ITEM'}-${timestamp}`;
    handleChange('sku', sku);
  };

  // Get selected account details for display
  const selectedAccount = chartOfAccounts?.find(account => account?.id === formData?.accountId);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-elevated w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">
            {item ? 'Edit Item' : 'Add New Item'}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose} disabled={isSubmitting}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <Icon name="AlertCircle" size={16} className="text-red-400 mr-2 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setError('')}
                className="ml-auto -mr-2 -mt-1"
              >
                <Icon name="X" size={14} />
              </Button>
            </div>
          </div>
        )}

        <div className="overflow-y-auto max-h-[calc(90vh-140px)] p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground">Basic Information</h3>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Item Name *
                  </label>
                  <Input
                    type="text"
                    value={formData?.itemName}
                    onChange={(e) => handleChange('itemName', e?.target?.value)}
                    placeholder="Enter item name"
                    error={errors?.itemName}
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData?.description}
                    onChange={(e) => handleChange('description', e?.target?.value)}
                    placeholder="Enter item description"
                    rows={3}
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none disabled:opacity-50"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      SKU {!item && <span className="text-xs text-muted-foreground">(auto-generated if empty)</span>}
                    </label>
                    <div className="flex space-x-2">
                      <Input
                        value={formData?.sku}
                        onChange={(e) => handleChange('sku', e?.target?.value)}
                        placeholder={item ? "Enter SKU" : "Leave empty to auto-generate"}
                        error={errors?.sku}
                        disabled={isSubmitting}
                      />
                      <Button type="button" variant="outline" onClick={generateSKU} disabled={isSubmitting}>
                        <Icon name="Wand2" size={16} />
                      </Button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Category *
                    </label>
                    <select
                      value={formData?.category || ""}
                      onChange={(e) => handleChange("category", e?.target?.value)}
                      disabled={isSubmitting}
                      className={`w-full px-3 py-2 border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 ${
                        errors?.category ? 'border-error' : 'border-border'
                      }`}
                      required
                    >
                      <option value="" disabled>Select category</option>
                      {categories?.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>

                    {errors?.category && (
                      <p className="mt-1 text-sm text-error">{errors?.category}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Unit of Measure
                    </label>
                    <select
                      value={formData?.unitOfMeasure}
                      onChange={(e) => handleChange('unitOfMeasure', e?.target?.value)}
                      disabled={isSubmitting}
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50"
                    >
                      <option value="each">Each</option>
                      <option value="box">Box</option>
                      <option value="pack">Pack</option>
                      <option value="kg">Kilogram</option>
                      <option value="lb">Pound</option>
                      <option value="hour">Hour</option>
                      <option value="day">Day</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Purchase Price *
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData?.purchasePrice}
                      onChange={(e) => handleChange('purchasePrice', e?.target?.value)}
                      placeholder="0.00"
                      error={errors?.purchasePrice}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Selling Price *
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData?.sellingPrice}
                      onChange={(e) => handleChange('sellingPrice', e?.target?.value)}
                      placeholder="0.00"
                      error={errors?.sellingPrice}
                      disabled={isSubmitting}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Tax Rate (%)
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData?.taxRate}
                      onChange={(e) => handleChange('taxRate', e?.target?.value)}
                      placeholder="0.00"
                      error={errors?.taxRate}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                {/* NEW: Account Selection */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Account Name
                  </label>
                  <select
                    value={formData?.accountId || ""}
                    onChange={(e) => handleChange("accountId", e?.target?.value)}
                    disabled={isSubmitting || loadingAccounts}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50"
                  >
                    <option value="">Select account (optional)</option>
                    {chartOfAccounts?.map((account) => (
                      <option key={account?.id} value={account?.id}>
                        {account?.account_code} - {account?.account_name}
                      </option>
                    ))}
                  </select>
                  {loadingAccounts && (
                    <p className="mt-1 text-xs text-muted-foreground">Loading accounts...</p>
                  )}
                </div>

                {/* Display selected account subtype */}
                {selectedAccount && (
                  <div className="p-3 bg-muted/20 border border-border rounded-md">
                    <p className="text-sm text-foreground">
                      <span className="font-medium">Account Subtype:</span> {selectedAccount?.account_subtype?.replace(/_/g, ' ')?.toUpperCase()}
                    </p>
                    {selectedAccount?.description && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {selectedAccount?.description}
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData?.trackInventory}
                      onChange={(e) => handleChange('trackInventory', e?.target?.checked)}
                      disabled={isSubmitting}
                      className="rounded border-border disabled:opacity-50"
                    />
                    <span className="text-sm font-medium text-foreground">
                      Track inventory for this item
                    </span>
                  </label>
                </div>
              </div>

              {/* Inventory & Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground">
                  Inventory & Details
                </h3>

                {formData?.trackInventory && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Weight per Unit *
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData?.weightPerUnit}
                      onChange={(e) => handleChange('weightPerUnit', e?.target?.value)}
                      placeholder="1"
                      error={errors?.weightPerUnit}
                      disabled={isSubmitting}
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Status
                  </label>
                  <select
                    value={formData?.isActive ? 'active' : 'inactive'}
                    onChange={(e) => handleChange('isActive', e?.target?.value === 'active')}
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Notes
                  </label>
                  <textarea
                    value={formData?.notes}
                    onChange={(e) => handleChange('notes', e?.target?.value)}
                    placeholder="Add any additional notes"
                    rows={3}
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none disabled:opacity-50"
                  />
                </div>
              </div>
            </div>
          </form>
        </div>

        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Icon name="Save" size={16} className="mr-2" />
                {item ? 'Update Item' : 'Save Item'}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ItemFormModal;